import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { formatNumber } from 'src/utils/numberFormatter';

import * as styles from './AnimatedNumber.css';

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (typeof savedCallback.current === 'function') {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

function formatForDisplay(number = 0, decimals: number): string[] {
  const locale = getLocaleFromLanguageOrCurrent();

  return formatNumber(number, decimals, locale.code, false).split('').reverse();
}

type NumberColumnProps = {
  digit: string;
  delta: 'increase' | 'decrease' | null;
  isScrolling: boolean;
};

function NumberColumn({ digit, delta, isScrolling }: NumberColumnProps) {
  const [position, setPosition] = useState(0);

  const columnContainer = useRef<HTMLDivElement>(null);

  const setColumnToNumber = () => {
    if (columnContainer.current) {
      const height = columnContainer.current.clientHeight;

      const parsed = parseInt(digit, 10);
      setPosition(height * parsed);
    }
  };

  useEffect(() => {
    setColumnToNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digit]);

  useEffect(() => {
    const el = columnContainer.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      setColumnToNumber();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digit]);

  return (
    <div className={styles.tickerColumnContainer} ref={columnContainer}>
      <motion.div
        initial={false}
        animate={{ y: position }}
        transition={{ duration: 0.1 }}
        className={clsx(styles.tickerColumn, {
          [styles.tickerColumnIncrease]: delta === 'increase' && !isScrolling,
          [styles.tickerColumnDecrease]: delta === 'decrease' && !isScrolling,
        })}
      >
        {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((num) => (
          <div key={num} className={styles.tickerDigit}>
            <span>{num}</span>
          </div>
        ))}
      </motion.div>
      <span className={styles.numberPlaceholder}>0</span>
    </div>
  );
}

export type AnimatingNumberProps = {
  value?: number | null;
  prefix?: string;
  isScrolling: boolean;
  decimals?: number;
};

// Adapted from this code sandbox: https://codesandbox.io/p/sandbox/robinhood-animating-number-mihcn
export default function AnimatingNumber({
  value,
  isScrolling,
  prefix,
  decimals = 2,
}: AnimatingNumberProps) {
  const numArray = formatForDisplay(value || 0, decimals);
  const previousNumber = usePrevious(value || 0);
  const formattedPrefix = prefix ? prefix.split('').reverse() : null;

  let delta: 'increase' | 'decrease' | null = null;

  const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  if (!value) {
    return null;
  }
  if (previousNumber !== undefined) {
    if (value > previousNumber) delta = 'increase';
    if (value < previousNumber) delta = 'decrease';
  }

  return (
    <motion.div className={styles.tickerView} initial={false}>
      {numArray.map((number, index) => {
        if (!digits.includes(number)) {
          return (
            <div key={index} className={styles.tickerDigit}>
              <span>{number}</span>
            </div>
          );
        }
        return (
          <NumberColumn
            key={index}
            digit={number}
            delta={delta}
            isScrolling={isScrolling}
          />
        );
      })}
      {formattedPrefix &&
        formattedPrefix.map((p, i) => (
          <span className={styles.tickerDigit} key={`prefix-${i}`}>
            {p}
          </span>
        ))}
    </motion.div>
  );
}
